import React, { useState, useEffect } from "react";
import {
  DialogTitle,
  Stack,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Switch,
  Tooltip,
} from "@mui/material";
import ModalLayout from "./Layout";
import ModalLayoutContent from "./Layout/Content";
import ModalLayoutFooter from "./Layout/Footer";
import ModalLayoutBtn from "./Layout/Btn";
import ModalLayoutInput from "./Layout/Input";
import { Key, X, Eye, EyeOff, Trash2, Edit, Plus, Info, ChevronLeft, ChevronRight } from "react-feather";
import {
  getSecretsService,
  createSecretService,
  updateSecretService,
  deleteSecretService,
} from "../../services/global";
import DatajoltSpinner from "../global/DatajoltSpinner";

// Secrets that pre-date this interface and were backfilled from AWS Secrets Manager.
// They are used by live pipelines, so the API rejects edits/deletes on them.
const LEGACY_SECRET_TYPE = "LEGACY";

// Secrets are paginated client-side in the list view.
const PAGE_SIZE = 10;

/**
 * SecretsModal - Manage user secrets with AWS Secrets Manager integration
 */
const SecretsModal = ({ isShown, onHide }) => {
  const [view, setView] = useState("list"); // 'list' | 'create' | 'edit'
  const [secrets, setSecrets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showValues, setShowValues] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0); // 0-indexed page in the list view

  // Form state
  const [formData, setFormData] = useState({
    id: null,
    secret_alias: "",
    secret_type: "CUSTOM",
    keyValuePairs: [{ key: '', value: '' }],
    encrypted: false,
  });

  // Helper to extract value from secret (handles both string and object)
  const getSecretValue = (secret) => {
    if (!secret.value) return "";
    if (typeof secret.value === "string") return secret.value;
    if (typeof secret.value === "object" && secret.value.value) {
      return secret.value.value;
    }
    return String(secret.value);
  };

  // Load secrets when modal opens
  useEffect(() => {
    if (isShown) {
      loadSecrets();
      setView("list");
      setPage(0);
      setError(null);
      setSuccess(null);
    }
  }, [isShown]);

  // Keep the current page in range as the list grows/shrinks (e.g. after a delete).
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(secrets.length / PAGE_SIZE) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [secrets.length, page]);

  const loadSecrets = async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await getSecretsService();
      setSecrets(data);
    } catch (err) {
      setError(err.error || "Failed to load secrets");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      id: null,
      secret_alias: "",
      secret_type: "CUSTOM",
      keyValuePairs: [{ key: '', value: '' }],
      encrypted: false,
    });
    setError(null);
    setSuccess(null);
    setView("create");
  };

  const handleEdit = (secret) => {
    // Convert backend format (object) to frontend format (array of {key, value})
    let keyValuePairs = [{ key: '', value: '' }];
    if (secret.key_value_pairs && typeof secret.key_value_pairs === 'object') {
      keyValuePairs = Object.entries(secret.key_value_pairs).map(([key, value]) => ({
        key,
        value: String(value),
      }));
    } else if (secret.value) {
      // Legacy format - single value
      keyValuePairs = [{ key: 'value', value: getSecretValue(secret) }];
    }

    setFormData({
      id: secret.id,
      secret_alias: secret.secret_alias,
      secret_type: secret.secret_type,
      keyValuePairs,
      encrypted: secret.encrypted,
    });
    setError(null);
    setSuccess(null);
    setView("edit");
  };

  const handleSave = async () => {
    // Validate: need alias and at least one complete key/value pair
    const validPairs = formData.keyValuePairs.filter(p => p.key.trim() && p.value.trim());
    if (!formData.secret_alias || validPairs.length === 0) {
      return;
    }

    // Convert array to object for backend
    const keyValuePairsObj = {};
    validPairs.forEach(({ key, value }) => {
      keyValuePairsObj[key.trim()] = value;
    });

    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const isUpdate = formData.id;
      if (isUpdate) {
        // Update existing secret
        await updateSecretService(formData.id, {
          key_value_pairs: keyValuePairsObj,
          encrypted: formData.encrypted,
        });
      } else {
        // Create new secret
        await createSecretService({
          secret_alias: formData.secret_alias,
          secret_type: formData.secret_type,
          key_value_pairs: keyValuePairsObj,
          encrypted: formData.encrypted,
        });
      }
      await loadSecrets();
      setSuccess(
        isUpdate
          ? "Secret updated successfully"
          : `Secret "${formData.secret_alias}" created successfully`
      );
      setView("list");
    } catch (err) {
      setError(err.error || "Failed to save secret");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (secretId) => {
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      await deleteSecretService(secretId);
      await loadSecrets();
      setSuccess("Secret deleted successfully");
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.error || "Failed to delete secret");
      setDeleteConfirm(null);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper functions for key/value pairs
  const addKeyValuePair = () => {
    setFormData({
      ...formData,
      keyValuePairs: [...formData.keyValuePairs, { key: '', value: '' }],
    });
  };

  const removeKeyValuePair = (index) => {
    if (formData.keyValuePairs.length > 1) {
      const newPairs = formData.keyValuePairs.filter((_, i) => i !== index);
      setFormData({ ...formData, keyValuePairs: newPairs });
    }
  };

  const updateKeyValuePair = (index, field, value) => {
    const newPairs = [...formData.keyValuePairs];
    newPairs[index] = { ...newPairs[index], [field]: value };
    setFormData({ ...formData, keyValuePairs: newPairs });
  };

  const renderListView = () => {
    const pageCount = Math.max(1, Math.ceil(secrets.length / PAGE_SIZE));
    const pagedSecrets = secrets.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    return (
    <>
      <ModalLayoutContent sx={{ padding: "24px 28px", minHeight: "300px" }}>
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ marginBottom: 2, fontSize: "12px" }}
          >
            {error}
          </Alert>
        )}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <DatajoltSpinner />
          </Box>
        ) : secrets.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
              gap: 2,
            }}
          >
            <Key size={48} color="#9e9e9e" />
            <Typography sx={{ color: "#757575", fontSize: "14px" }}>
              No secrets found. Create your first secret to get started.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "11px" }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "11px" }}>
                    Keys
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontSize: "11px" }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedSecrets.map((secret) => (
                  <TableRow key={secret.id} hover>
                    <TableCell sx={{ fontSize: "11px" }}>
                      {secret.secret_alias}
                    </TableCell>
                    <TableCell sx={{ fontSize: "11px" }}>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "monospace",
                          color: secret.encrypted ? "#757575" : "#333",
                          fontSize: "11px",
                        }}
                      >
                        {secret.value_preview || `${secret.key_count || 1} key${(secret.key_count || 1) !== 1 ? 's' : ''}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(secret)}
                        sx={{ padding: "4px", marginRight: "4px" }}
                      >
                        <Edit size={14} />
                      </IconButton>
                      {/* Legacy secrets feed live pipelines and cannot be deleted from the UI
                          (the AWS delete is irreversible); they remain editable. */}
                      {secret.secret_type === LEGACY_SECRET_TYPE ? (
                        <Tooltip
                          title="Legacy secret — editable, but can't be deleted here"
                          placement="left"
                          arrow
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled
                              sx={{ padding: "4px", color: "#d32f2f" }}
                            >
                              <Trash2 size={14} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirm(secret)}
                          sx={{ padding: "4px", color: "#d32f2f" }}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && pageCount > 1 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={{ marginTop: 2 }}
          >
            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              sx={{ padding: "4px" }}
            >
              <ChevronLeft size={16} />
            </IconButton>
            <Typography sx={{ fontSize: "11px", color: "#495057" }}>
              Page {page + 1} of {pageCount}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={page >= pageCount - 1}
              sx={{ padding: "4px" }}
            >
              <ChevronRight size={16} />
            </IconButton>
          </Stack>
        )}
        {success && (
          <Alert
            severity="success"
            onClose={() => setSuccess(null)}
            sx={{ marginTop: 2, fontSize: "12px" }}
          >
            {success}
          </Alert>
        )}
      </ModalLayoutContent>
      <ModalLayoutFooter sx={{ padding: "16px 24px" }}>
        <ModalLayoutBtn
          color="primary"
          onClick={handleCreate}
          variant="contained"
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            background: "linear-gradient(45deg, #3f51b5, #5c6bc0)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(63,81,181,0.3)",
            },
          }}
        >
          <Plus size={16} style={{ marginRight: "6px" }} />
          Create Secret
        </ModalLayoutBtn>
      </ModalLayoutFooter>
    </>
    );
  };

  const renderFormView = () => (
    <>
      <ModalLayoutContent sx={{ padding: "24px 28px" }}>
        <Stack spacing={2}>
          {/* Secret Alias */}
          <Box>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                marginBottom: "8px",
                color: "#495057",
              }}
            >
              Secret Name *
            </Typography>
            <ModalLayoutInput
              type="text"
              placeholder="e.g., Facebook_tokens"
              value={formData.secret_alias}
              onChange={(e) =>
                setFormData({ ...formData, secret_alias: e.target.value })
              }
              disabled={formData.id !== null}
              sx={{
                fontSize: "11px",
                borderRadius: "8px",
                border: "1px solid rgba(0,0,0,0.1)",
                padding: "10px 16px",
                width: "100%",
                backgroundColor: formData.id ? "#f5f5f5" : "white",
              }}
            />
            {formData.id !== null && (
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "#757575",
                  fontStyle: "italic",
                  marginTop: "4px",
                }}
              >
                Name cannot be changed after creation
              </Typography>
            )}
          </Box>

          {/* Key/Value Pairs */}
          <Box>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                marginBottom: "8px",
                color: "#495057",
              }}
            >
              Key/Value Pairs *
            </Typography>
            <Stack spacing={1}>
              {formData.keyValuePairs.map((pair, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <ModalLayoutInput
                    type="text"
                    placeholder="Key (e.g., token)"
                    value={pair.key}
                    onChange={(e) => updateKeyValuePair(index, 'key', e.target.value)}
                    sx={{
                      fontSize: "11px",
                      borderRadius: "8px",
                      border: "1px solid rgba(0,0,0,0.1)",
                      padding: "8px 12px",
                      flex: 1,
                    }}
                  />
                  <ModalLayoutInput
                    type={showValues[`pair-${index}`] ? "text" : "password"}
                    placeholder="Value"
                    value={pair.value}
                    onChange={(e) => updateKeyValuePair(index, 'value', e.target.value)}
                    sx={{
                      fontSize: "11px",
                      borderRadius: "8px",
                      border: "1px solid rgba(0,0,0,0.1)",
                      padding: "8px 12px",
                      flex: 1,
                      fontFamily: "monospace",
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowValues((prev) => ({
                                ...prev,
                                [`pair-${index}`]: !prev[`pair-${index}`],
                              }))
                            }
                            edge="end"
                            size="small"
                          >
                            {showValues[`pair-${index}`] ? (
                              <EyeOff size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {formData.keyValuePairs.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeKeyValuePair(index)}
                      sx={{ padding: "4px", color: "#d32f2f" }}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  )}
                </Stack>
              ))}
              <Button
                size="small"
                onClick={addKeyValuePair}
                sx={{
                  fontSize: "10px",
                  textTransform: "none",
                  color: "#3f51b5",
                  alignSelf: "flex-start",
                  padding: "4px 8px",
                }}
              >
                <Plus size={12} style={{ marginRight: "4px" }} />
                Add Key/Value Pair
              </Button>
            </Stack>
          </Box>

          {/* Encrypt Toggle with Tooltip */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Switch
              checked={formData.encrypted}
              onChange={(e) =>
                setFormData({ ...formData, encrypted: e.target.checked })
              }
              size="small"
            />
            <Typography
              sx={{
                fontSize: "12px",
                color: "#333",
              }}
            >
              Encrypt?
            </Typography>
            <Tooltip
              title="Secret variable values will not be visible again after saving"
              placement="top"
              arrow
            >
              <Box sx={{ display: "flex", alignItems: "center", cursor: "help" }}>
                <Info size={14} color="#757575" />
              </Box>
            </Tooltip>
          </Box>

          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mt: 1, fontSize: "11px", "& .MuiAlert-message": { fontSize: "11px" } }}
            >
              {error}
            </Alert>
          )}
        </Stack>
      </ModalLayoutContent>
      <ModalLayoutFooter sx={{ padding: "16px 24px", gap: 1 }}>
        <ModalLayoutBtn
          color="inherit"
          onClick={() => setView("list")}
          disabled={submitting}
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            color: "#495057",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Cancel
        </ModalLayoutBtn>
        <ModalLayoutBtn
          color="primary"
          onClick={handleSave}
          disabled={
            submitting || !formData.secret_alias || !formData.keyValuePairs.some(p => p.key.trim() && p.value.trim())
          }
          variant="contained"
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            background: "linear-gradient(45deg, #3f51b5, #5c6bc0)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(63,81,181,0.3)",
            },
            "&:disabled": {
              background: "#b0bec5",
              color: "#ffffff",
            },
          }}
        >
          {submitting ? (
            <>
              Saving...&nbsp;
              <CircularProgress size={16} color="inherit" />
            </>
          ) : (
            <>{formData.id ? "Update Secret" : "Create Secret"}</>
          )}
        </ModalLayoutBtn>
      </ModalLayoutFooter>
    </>
  );

  return (
    <>
      <ModalLayout
        isShown={isShown}
        onHide={onHide}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            overflow: "hidden",
            background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
            border: "1px solid rgba(0,0,0,0.08)",
            minWidth: "600px",
          },
        }}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          position={"relative"}
          padding={"16px 24px"}
          sx={{ background: "rgba(235,245,255,0.6)" }}
        >
          <DialogTitle
            padding={"0 !important"}
            sx={{
              color: "#3f51b5",
              fontWeight: 800,
              fontSize: "11px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Key size={14} style={{ marginRight: "6px" }} />
            {view === "list"
              ? "Manage Secrets"
              : view === "create"
              ? "Create New Secret"
              : "Edit Secret"}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={onHide}
            sx={{
              position: "absolute",
              right: 16,
              top: 10,
              color: "#495057",
              "&:hover": {
                background: "rgba(0,0,0,0.05)",
                transform: "rotate(90deg)",
                transition: "transform 0.3s ease",
              },
            }}
          >
            <X size={16} />
          </IconButton>
        </Stack>

        {view === "list" ? renderListView() : renderFormView()}
      </ModalLayout>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        sx={{ zIndex: 10001 }}
      >
        <Stack
          justifyContent={"space-between"}
          position={"relative"}
          padding={"16px 24px"}
          borderBottom={"1px solid rgba(0,0,0,0.05)"}
          sx={{ background: "rgba(255,235,238,0.6)" }}
        >
          <DialogTitle
            padding={"0 !important"}
            sx={{
              color: "#d32f2f",
              fontWeight: 800,
              fontSize: "11px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Trash2 size={14} style={{ marginRight: "6px" }} />
            Delete Secret
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setDeleteConfirm(null)}
            sx={{
              position: "absolute",
              right: 16,
              top: 10,
              color: "#495057",
              "&:hover": {
                background: "rgba(0,0,0,0.05)",
                transform: "rotate(90deg)",
                transition: "transform 0.3s ease",
              },
            }}
          >
            <X size={16} />
          </IconButton>
        </Stack>
        <DialogContent>
          <Typography sx={{ fontSize: "12px", marginBottom: 1 }}>
            Are you sure you want to delete the secret{" "}
            <strong>"{deleteConfirm?.secret_alias}"</strong>?
          </Typography>
          <Typography
            sx={{ fontSize: "11px", color: "#d32f2f", fontStyle: "italic", marginBottom: 2 }}
          >
            This will permanently remove the secret from Secrets Manager.
          </Typography>
          <Box sx={{ borderBottom: "1px solid #dee6ed", marginBottom: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirm(null)}
            disabled={submitting}
            variant="contained"
            color="inherit"
            sx={{
              fontSize: "11px",
              borderRadius: "8px",
              fontWeight: 600,
              textTransform: "none",
              padding: "8px 20px",
              color: "#495057",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirm.id)}
            disabled={submitting}
            color="error"
            variant="contained"
            sx={{
              fontSize: "11px",
              borderRadius: "8px",
              fontWeight: 600,
              textTransform: "none",
              padding: "8px 20px",
            }}
          >
            {submitting ? (
              <>
                Deleting...&nbsp;
                <CircularProgress size={14} color="inherit" />
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SecretsModal;
